import React from 'react';
import { injectIntl } from 'react-intl';
import { Grid, IconButton, Typography } from '@mui/material';
import Pyramid from './Pyramid/Pyramid';
import { PYRAMID_COLORS } from '../constants';
import ColoredCurrency from '../ColoredCurrency/ColoredCurrency';
import ColoredRatio from '../ColoredRatio/ColoredRatio';
import GaugeChart from './GaugeChart/GaugeChart';
import * as echarts from 'echarts';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import { getWebImageUrl } from '../../../../utils/image';

const HeadingTypoStyle = {
  fontSize: '180%',
  color: 'var(--new-theme)'
};

const FullHeightStyle = {
  height: '100%'
};

const LeftRegionItemStyle = {
  // flexBasis: '24%',
  height: 68,
  textAlign: 'center'
};

const ArrowIconStyle = {
  background: '#3966f8',
  color: '#fff',
  borderRadius: '50%',
  fontSize: 32
};

// Decorative hill/piggy backdrop shown behind the pyramid in the web UI only
// (not in the PDF export, i.e. when `template` is set). A translucent white
// overlay is layered on top of the image to lighten it so the pyramid and
// figures stay legible; `cover` keeps the image's aspect ratio (no stretching).
const PyramidBackgroundStyle = {
  backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.82), rgba(255, 255, 255, 0.82)), url("${getWebImageUrl(
    'img/financial-pyramid/pyramid-bg.png'
  )}")`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  borderRadius: 16,
  overflow: 'hidden'
};

const AssetItem = props => {
  const { warn, percentage, currency, value, color, fontSize } = props;

  return (
    <Grid container spacing={0} wrap="nowrap" sx={{ flexDirection: 'column', justifyContent: 'flex-start' }}>
      <Grid style={{ minWidth: 130 }}>
        <ColoredRatio warn={warn} value={percentage} color={color} />
      </Grid>
      <Grid style={{ flexGrow: 1, minWidth: 130 }}>
        <ColoredCurrency currency={currency} value={value} color={color} fontSize={fontSize} />
      </Grid>
    </Grid>
  );
};

const Column = props => {
  const { items } = props;
  return (
    <Grid container wrap="nowrap" style={FullHeightStyle} sx={{ flexDirection: 'column', justifyContent: 'center' }}>
      {(items || []).map((item, index) => (
        <Grid key={index} style={LeftRegionItemStyle}>
          <Grid
            container
            style={{ ...FullHeightStyle, minWidth: 130, padding: 8 }}
            sx={{
              alignItems: 'flex-end',
              justifyContent: 'center'
            }}
          >
            <Grid>{item}</Grid>
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
};

const PyramidSection = props => {
  const {
    intl,
    currency,
    pyramidData,
    monthlyIncome,
    template,
    showIncome,
    showRecord,
    onChangeShowIncome,
    onChangeShowRecord
  } = props;

  const isFirstWarning = pyramidData.risk.asset > pyramidData.investment.asset;
  const isSecondWarning = pyramidData.investment.asset > pyramidData.saving.asset;

  return (
    <Grid container sx={{ flexDirection: 'column', justifyContent: 'center' }}>
      <div className={!template ? 'pyramid-backdrop' : undefined} style={!template ? PyramidBackgroundStyle : undefined}>
      <Grid style={{ marginTop: 50, marginBottom: 20, whiteSpace: 'nowrap' }}>
        <Grid container>
          <Grid style={{ width: 'calc((100% - 250px)/2)' }}>
            <Grid
              container
              spacing={1}
              sx={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
            >
              {!template && (
                <Grid>
                  <IconButton
                    color="primary"
                    onClick={onChangeShowIncome}
                    style={{
                      transition: 'transform 0.8s',
                      transform: `rotate(${!showIncome ? -90 : 90}deg)`,
                      padding: 0,
                      boxShadow:
                        '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)'
                    }}
                    size="large"
                  >
                    <ArrowDropDownRoundedIcon style={ArrowIconStyle} />
                  </IconButton>
                </Grid>
              )}
              <Grid>
                <Typography color="primary" align="center" style={HeadingTypoStyle}>
                  {intl.formatMessage({ id: 'asset' })}
                </Typography>
                <Typography color="textSecondary" align="center">
                  ({intl.formatMessage({ id: currency })})
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid style={{ width: 250 }} />
          <Grid style={{ width: 'calc((100% - 250px)/2)' }}>
            <Grid
              container
              spacing={1}
              sx={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
            >
              {!template && (
                <Grid>
                  <IconButton
                    color="primary"
                    onClick={onChangeShowRecord}
                    style={{
                      transition: 'transform 0.8s',
                      transform: `rotate(${!showRecord ? 90 : -90}deg)`,
                      padding: 0,
                      boxShadow:
                        '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)'
                    }}
                    size="large"
                  >
                    <ArrowDropDownRoundedIcon style={ArrowIconStyle} />
                  </IconButton>
                </Grid>
              )}
              <Grid>
                <Typography color="primary" align="center" style={HeadingTypoStyle}>
                  {intl.formatMessage({ id: 'monthly-payment' })}
                </Typography>
                <Typography color="textSecondary" align="center">
                  ({intl.formatMessage({ id: currency })})
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid style={{ height: 300 }}>
        <Grid
          container
          style={FullHeightStyle}
          sx={{
            alignItems: 'flex-start'
          }}
        >
          <Grid
            style={{ ...FullHeightStyle, width: 'calc((100% - 250px)/2)', cursor: 'pointer' }}
            onClick={onChangeShowIncome}
          >
            <Column
              items={[
                <AssetItem
                  warn={isFirstWarning}
                  currency={currency}
                  value={pyramidData.risk.asset}
                  percentage={pyramidData.risk.assetPercentage}
                  color={PYRAMID_COLORS[0]}
                />,
                <AssetItem
                  warn={isFirstWarning || isSecondWarning}
                  currency={currency}
                  value={pyramidData.investment.asset}
                  percentage={pyramidData.investment.assetPercentage}
                  color={PYRAMID_COLORS[1]}
                />,
                <AssetItem
                  warn={isSecondWarning}
                  currency={currency}
                  value={pyramidData.saving.asset}
                  percentage={pyramidData.saving.assetPercentage}
                  color={PYRAMID_COLORS[2]}
                />,
                <Grid
                  container
                  style={{ minWidth: 130, marginTop: 19 }}
                  sx={{ flexDirection: 'column', justifyContent: 'center' }}
                >
                  <Grid>
                    <Typography color="textSecondary" align="left">
                      {`${intl.formatMessage({ id: 'Coverage Amount' })}:`}
                    </Typography>
                  </Grid>
                  <Grid>
                    <ColoredCurrency
                      currency={currency}
                      value={pyramidData.protection.asset}
                      color={PYRAMID_COLORS[3]}
                      fontSize={'150%'}
                      bold
                    />
                  </Grid>
                </Grid>
              ]}
            />
          </Grid>
          <Grid style={{ ...FullHeightStyle, width: 250 }}>
            <Pyramid />
          </Grid>
          <Grid
            style={{ ...FullHeightStyle, width: 'calc((100% - 250px)/2)', cursor: 'pointer' }}
            onClick={onChangeShowRecord}
          >
            <Column
              items={[
                // <AssetItem
                // 	currency={currency}
                // 	value={pyramidData.risk.monthlyPayment}
                // 	percentage={pyramidData.risk.monthlyPaymentPercentage}
                // 	color={PYRAMID_COLORS[0]}
                // 	fontSize={'110%'}
                // />,
                // <AssetItem
                // 	currency={currency}
                // 	value={pyramidData.investment.monthlyPayment}
                // 	percentage={pyramidData.investment.monthlyPaymentPercentage}
                // 	color={PYRAMID_COLORS[1]}
                // 	fontSize={'110%'}
                // />,
                // <AssetItem
                // 	currency={currency}
                // 	value={pyramidData.saving.monthlyPayment}
                // 	percentage={pyramidData.saving.monthlyPaymentPercentage}
                // 	color={PYRAMID_COLORS[2]}
                // 	fontSize={'110%'}
                // />,
                // <AssetItem
                // 	currency={currency}
                // 	value={pyramidData.protection.monthlyPayment}
                // 	percentage={pyramidData.protection.monthlyPaymentPercentage}
                // 	color={PYRAMID_COLORS[3]}
                // 	fontSize={'110%'}
                // />,

                <ColoredCurrency
                  currency={currency}
                  value={pyramidData.risk.monthlyPayment}
                  color={PYRAMID_COLORS[0]}
                  fontSize={'150%'}
                />,
                <ColoredCurrency
                  currency={currency}
                  value={pyramidData.investment.monthlyPayment}
                  color={PYRAMID_COLORS[1]}
                  fontSize={'150%'}
                />,
                <ColoredCurrency
                  currency={currency}
                  value={pyramidData.saving.monthlyPayment}
                  color={PYRAMID_COLORS[2]}
                  fontSize={'150%'}
                />,
                <Grid
                  container
                  style={{ width: 'auto', marginTop: 19 }}
                  sx={{ flexDirection: 'column', justifyContent: 'center' }}
                >
                  <Grid>
                    <Typography color="textSecondary" align="left">
                      {`${intl.formatMessage({ id: 'Premium' })}:`}
                    </Typography>
                  </Grid>
                  <Grid>
                    <ColoredCurrency
                      currency={currency}
                      value={pyramidData.protection.monthlyPayment}
                      color={PYRAMID_COLORS[3]}
                      fontSize="150%"
                      bold
                    />
                  </Grid>
                </Grid>
              ]}
            />
          </Grid>
        </Grid>
      </Grid>
      </div>
      {showRecord && (
        <Grid style={{ marginTop: 85, marginBottom: !template ? -36 : 0 }}>
          <Grid container spacing={1}>
            <Grid size={6}>
              <GaugeChart
                className="saving-income-percent-chart"
                width={template ? 180 : '50%'}
                label={intl.formatMessage({ id: 'fin-py-saving-income-per' })}
                value={pyramidData.saving.monthlyPayment}
                max={(monthlyIncome || 0) * 0.6}
                percentage={monthlyIncome ? (pyramidData.saving.monthlyPayment / monthlyIncome) * 100 : 0}
                maxPercentage={60}
                insufficientPercentage={20}
                colors={[
                  [
                    1,
                    new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                      {
                        offset: 0,
                        color: '#ff0000'
                      },
                      {
                        offset: 0.3,
                        color: '#ffa600'
                      },
                      {
                        offset: 0.7,
                        color: '#3eda7d'
                      }
                    ])
                  ]
                ]}
                axisLabelFormatter={v => {
                  switch (v) {
                    case 0:
                      return 0;
                    case 30:
                      return 20;
                    case 70:
                      return 40;
                    case 100:
                      return 60;
                    default:
                      return '';
                  }
                }}
              />
            </Grid>
            <Grid size={6}>
              <GaugeChart
                className="premium-income-percent-chart"
                width={template ? 180 : '50%'}
                label={intl.formatMessage({ id: 'fin-py-premium-income-per' })}
                value={pyramidData.protection.monthlyPayment}
                max={(monthlyIncome || 0) * 0.3}
                percentage={monthlyIncome ? (pyramidData.protection.monthlyPayment / monthlyIncome) * 100 : 0}
                maxPercentage={30}
                insufficientPercentage={10}
                colors={[
                  [
                    1,
                    new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                      {
                        offset: 0,
                        color: '#ff0000'
                      },
                      {
                        offset: 0.16667,
                        color: '#ffa600'
                      },
                      {
                        offset: 0.5,
                        color: '#3eda7d'
                      }
                    ])
                  ]
                ]}
                axisLabelFormatter={v => {
                  switch (v) {
                    case 0:
                      return 0;
                    case 30:
                      return 10;
                    case 70:
                      return 20;
                    case 100:
                      return 30;
                    default:
                      return '';
                  }
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
      {/*<Grid>*/}
      {/*    <Grid container spacing={2} sx={{ alignItems: "center" }}>*/}
      {/*        <Grid size={3}>*/}
      {/*            <Typography color="textSecondary" align="center">*/}
      {/*                {intl.formatMessage({ id: 'fin-py-premium-income-per' })}*/}
      {/*            </Typography>*/}
      {/*        </Grid>*/}
      {/*        <Grid size={6}>*/}
      {/*            <div style={{ height: 30 }}>*/}
      {/*                <MonthlyPaymentProgressBar*/}
      {/*                    monthlyPayment={pyramidData.protection.monthlyPayment}*/}
      {/*                    monthlyIncome={monthlyIncome}*/}
      {/*                />*/}
      {/*            </div>*/}
      {/*        </Grid>*/}
      {/*        <Grid size={3}>*/}
      {/*            <Typography color="primary" align="center">*/}
      {/*                {formatPremiumIncomePercentageDisplay(pyramidData.protection.premiumIncomePercentage)}*/}
      {/*            </Typography>*/}
      {/*        </Grid>*/}
      {/*    </Grid>*/}
      {/*</Grid>*/}
    </Grid>
  );
};

export default injectIntl(PyramidSection);
